import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  FacilityList,
  FacilityMetaData,
  FacilityData,
} from '../services/FacilityService';
import {
  getFacilityList,
  getFacilityMetaData,
  saveOrUpdateFacility,
  getFacilityDetailsById,
  getFacilityClinicianMappedList,
  checkShotNameUniq,
} from '../services/FacilityService';

export interface FacilityListQueryPerams {
  userId: string;
  page?: number;
  size?: number;
  sortField?: string;
  sortType?: string;
  searchTerm?: string;
  statusId?: number[];
  partnerId?: number;
  clinicianId?: number;
}

export type shotNameCheckResult = {
  isAvailable: boolean;
  message?: string;
};

export const useGetFacilityList = ({
  partnerId,
  clinicianId,
  page = 0,
  size = 10,
  sortField,
  sortType,
  searchTerm,
  statusId,
}: FacilityListQueryPerams) => {
  const { data, isLoading, isError, error, refetch } = useQuery<FacilityList>({
    queryKey: [
      'FacilityListResponse',
      partnerId,
      clinicianId,
      page,
      size,
      sortField,
      sortType,
      searchTerm,
      statusId,
    ],
    queryFn: () =>
      getFacilityList({
        partnerId,
        clinicianId,
        page,
        size,
        sortField,
        sortType,
        searchTerm,
        statusId,
      }),
    staleTime: 0,
    gcTime: 0,
  });

  return {
    FacilityListResponse: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useGetFacilityMetaData = (userId: string) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<FacilityMetaData>({
      queryKey: ['FacilityMetaData', userId],
      queryFn: async () => {
        const data = await getFacilityMetaData(userId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
    });

  return {
    facilityMetaData: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useSaveOrUpdateFacility = (onSuccess: () => void) => {
  const queryClient = useQueryClient();
  return useMutation<
    FacilityData,
    Error,
    FacilityData & { userId: string; action: string }
  >({
    mutationFn: async ({ userId, action, ...payload }) => {
      const data = await saveOrUpdateFacility(userId, action, payload);
      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['FacilityListResponse'],
      });
      onSuccess();
    },
    onError: (error) => {
      console.error('Error saving or updating facility:', error);
    },
  });
};

export const useGetFacilityDetailsById = (
  userId: string,
  facilityId: number
) => {
  const { data, isLoading, isError, error, refetch } = useQuery<FacilityData>({
    queryKey: ['FacilityDetailsById', userId, facilityId],
    queryFn: async () => {
      const data = await getFacilityDetailsById(userId, facilityId);
      return data;
    },
    staleTime: 0,
    gcTime: 0,
    enabled: !!facilityId,
  });

  return {
    FacilityDetailsById: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useGetFacilityClinicanManagerList = ({
  clinicianId,
  page = 0,
  size = 10,
  sortField,
  sortType,
  searchTerm,
}: FacilityListQueryPerams) => {
  const { data, isLoading, isError, error, refetch } = useQuery<FacilityList>({
    queryKey: [
      'FacilityClinicianManagerListResponse',
      clinicianId,
      page,
      size,
      sortField,
      sortType,
      searchTerm,
    ],
    queryFn: async () => {
      const data = await getFacilityClinicianMappedList(
        clinicianId,
        page,
        size,
        sortField,
        sortType,
        searchTerm
      );
      return data;
    },
    staleTime: 0,
    gcTime: 0,
    enabled: !!clinicianId,
  });

  return {
    FacilityClinicianManagerListResponse: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useCheckShotNameUniq = (
  username: string,
  facilityUserId?: number
) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<shotNameCheckResult>({
      queryKey: ['ShotNameUniq', username, facilityUserId],
      queryFn: async () => {
        const data = await checkShotNameUniq(username, facilityUserId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
      enabled: !!username,
    });

  return {
    ShotNameUniq: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};
